import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Layout } from "antd";
import { useParams } from "react-router-dom";
import { useConfig } from "./data/useConfig";
import { useSearchResults } from "./data/useSearchResults";
import useMobileDetector from "./utils/useMobileDetector";
import Filters from "./components/Filters";
import Header from "./components/Header";
import NotFound from "./components/ResultsPage/NotFound";
import ResultsPage from "./components/ResultsPage";
import SearchInput from "./components/SearchInput/Input";
import Title from "./components/Title/Title";
import "./App.less";

const { Sider, Content } = Layout;

const CustomOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: none;
  z-index: 1;
  pointer-events: none;
`;

const CustomContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 30px;

  .ant-layout-sider-zero-width-trigger {
    display: ${(props) => (!props.collapsed ? "none" : "block")};
  }

  .beon-showcase--grid .beon-showcase__item .beon-showcase__item-image img {
    width: auto !important;
    height: auto !important;
  }

  ${({ customCss }) => css`
    ${customCss}
  `}
`;

const SearchArea = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
`;

const NoTermSearch = styled.div`
  h2 {
    font-size: 20px;
    text-align: center;
    margin-top: 40px;
    margin-bottom: 40px;
  }
  margin-bottom: 40px;
`;

// layout type
// sidebar | fullwidth
const LayoutApp = ({
  layoutType = "sidebar",
  enableButton = false,
  onlyInput,
}) => {
  const FULL_WIDTH = layoutType === "fullwidth";
  const [collapsed, setCollapsed] = useState(true);
  const { isMobile, isDesktop } = useMobileDetector();
  const { setId, searchPageCustomCss, show_sandbox_input, filterDirection } =
    useConfig();
  const {
    anchors,
    error,
    resultsData,
    searchTerm,
    setAnchors,
    setSearchTerm,
    showFilters,
    fetchResults,
    page,
    sortKey,
    selectedFilters,
    setIsLoadingFilters,
  } = useSearchResults();

  let { id } = useParams();
  if (window.bn_config_search_page.tenant_id) {
    id = window.bn_config_search_page.tenant_id;
  }

  const anchorsInWindow = window?.bn_config_search_page?.anchors;
  const searchTermInWindow = window?.bn_config_search_page?.search_term;

  const closeSidebar = () => {
    setCollapsed(true);
  };

  useEffect(() => {
    if (id) {
      setId(id);
    }
  }, [setId, id]);

  useEffect(() => {
    if (anchorsInWindow) {
      setAnchors(anchorsInWindow);
    }

    if (searchTermInWindow) {
      setSearchTerm(searchTermInWindow);
    }
  }, []);

  useEffect(() => {
    setIsLoadingFilters(true);
  }, [searchTerm]);

  useEffect(() => {
    if (onlyInput) return;
    fetchResults();
  }, [searchTerm, anchors, page, sortKey, selectedFilters]);

  if (!id) return null;

  if (onlyInput) return <SearchInput onSubmit={true} />;

  if (
    !anchors?.length &&
    !searchTerm &&
    !searchTermInWindow &&
    !anchorsInWindow
  ) {
    return (
      <NoTermSearch>
        <h2>Faça sua busca abaixo:</h2>
        <SearchInput onSubmit={false} noTerm={true} />
      </NoTermSearch>
    );
  }

  if (resultsData?.meta?.results_count === 0 || error) {
    return (
      <NoTermSearch>
        <NotFound />
      </NoTermSearch>
    );
  }

  const renderFilters = () => (
    <Filters
      closeSidebar={closeSidebar}
      collapsed={collapsed}
      layoutType={isMobile ? "sidebar" : layoutType}
      showLabel={FULL_WIDTH ? !isMobile : false}
      enableButton={enableButton}
    />
  );

  const renderSider = () => (
    <Sider
      theme="light"
      collapsed={collapsed}
      breakpoint="lg"
      collapsedWidth="0"
      width={isMobile ? "100vw" : 250}
      onCollapse={(collapsed) => setCollapsed(collapsed)}
    >
      {renderFilters()}
    </Sider>
  );

  return (
    <CustomContainer
      className={`bnly-search__page-container ${
        collapsed ? "bnly-sider-collapsed" : "bnly-sider-open"
      }`}
      collapsed={collapsed}
      customCss={searchPageCustomCss}
    >
      {filterDirection === "horizontal" && isDesktop && (
        <CustomOverlay className="bnly-search__overlay" />
      )}

      {show_sandbox_input && (
        <SearchArea>
          <SearchInput />
        </SearchArea>
      )}

      <Title isMobile={isMobile} />

      <Layout>
        {filterDirection !== "horizontal" &&
          showFilters &&
          (FULL_WIDTH && isDesktop ? renderFilters() : renderSider())}

        {filterDirection === "horizontal" &&
          (isMobile ? renderSider() : renderFilters())}

        <Content>
          <Header />
          <ResultsPage />
        </Content>
      </Layout>
    </CustomContainer>
  );
};

export default LayoutApp;
